import {
  BloomEffect,
  ChromaticAberrationEffect,
  EffectComposer,
  EffectPass,
  LUT3DEffect,
  RenderPass,
  ShockWaveEffect,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import * as THREE from 'three';
import { LUT_SIZE, writeLut } from './art/grading';
import { worldGrade } from './WorldGrade';

/* ═══════════════════════════════════════════════════════════════════════════
   Le pipeline de post-processing — UNIQUE point de contact avec la
   bibliothèque `postprocessing` (PLAN.md §3.2).

   Tout passe par ces quelques méthodes. Le jour où il faudra vendorer la 6.x
   — la licence Zlib l'autorise, et le bus factor du projet amont est de 1 —
   c'est le seul fichier à rouvrir. Le coût de sortie est ainsi borné par
   construction, ce qui rend l'arbitrage « on ship sur three r185 » assumé
   plutôt que subi.

   La chaîne réelle, avec son compte honnête de passes plein écran :

     [P0] scène OPAQUE      → composer.inputBuffer  (HalfFloat, taille MAX)
     [P3] EffectPass fusionné : Bloom · Chromatic · ACES · LUT · Vignette
     [P4] EffectPass          : SMAA
     [P5] scène OVERLAY, autoClear = false — projectiles ennemis, joueur,
          point de hitbox. Rendue APRÈS tout le post-processing.

   P1/P2 (target VFX en demi-résolution + composite additif) arrivent au J5
   avec les explosions : tant qu'aucune particule n'existe, une passe de plus
   ne ferait que consommer de la bande passante.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface VfxOptions {
  /** Tier BAS : bloom mono-passe, ni chromatic ni SMAA. */
  readonly lowTier?: boolean;
}

/* Réglages de l'onde de choc, partagés entre la création des effets et le calcul
   de leur durée de vie. `ShockWaveEffect` s'anime jusqu'à ce que son rayon
   atteigne `(maxRadius + waveSize) * 2`, en avançant de `delta × speed` par
   frame : la durée réelle d'un balayage est donc `(maxRadius + waveSize)·2/speed`
   secondes. La passe doit rester active AU MOINS aussi longtemps, sinon l'onde
   est coupée en plein vol — la panne exacte qui la rendait invisible. */
const SHOCK_SPEED = 2.4;
const SHOCK_WAVE_SIZE = 0.18;
function shockLifetime(maxRadius: number): number {
  return ((maxRadius + SHOCK_WAVE_SIZE) * 2) / SHOCK_SPEED + 0.1;
}

export class VfxPipeline {
  readonly composer: EffectComposer;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly bloom: BloomEffect;
  private readonly overlayScene: THREE.Scene;
  private readonly camera: THREE.Camera;
  /** ⚠️ UNE seule texture, pour toujours. Réécrire ses OCTETS ne change aucun
   *  `#define` ; lui substituer un autre objet ferait recalculer les defines de
   *  `LUT3DEffect` (taille, précision, domaine) et RECOMPILERAIT l'uber-pass —
   *  un freeze au franchissement de monde, c'est-à-dire juste après un boss. */
  private readonly lut: THREE.Data3DTexture;
  private readonly lutData: Uint8Array;

  /** Couche 3 des explosions — l'onde de choc (PLAN.md §4.7). Un POOL fixe de
   *  quatre ondes écran, jamais plus : au-delà, l'écran devient illisible. Elles
   *  vivent dans leur PROPRE passe, désactivée au repos pour ne rien coûter tant
   *  qu'aucune explosion n'en réclame. Absentes en tier bas (levier de coupe
   *  §4.9), d'où le `| null`. */
  private readonly shocks: readonly ShockWaveEffect[];
  private readonly shockPass: EffectPass | null;
  private shockCursor = 0;
  /** Secondes restantes pendant lesquelles la passe d'onde reste active. */
  private shockTimer = 0;

  /** Dimensions MAXIMALES. Tous les render targets sont alloués une fois à
   *  cette taille et ne sont jamais réalloués. */
  private maxW = 0;
  private maxH = 0;
  private scale = 1;
  private warnedUnsized = false;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    overlayScene: THREE.Scene,
    camera: THREE.Camera,
    opts: VfxOptions = {},
  ) {
    this.renderer = renderer;
    this.overlayScene = overlayScene;
    this.camera = camera;

    /* ⚠️ PIÈGE N°2 DU PLAN (§4.1) — le framebuffer LDR.
       Sans `HalfFloatType`, tout est clampé à 1.0 : le bloom devient binaire
       (un objet bloome ou pas, sans dégradé), `emissiveIntensity` 2 et 8
       rendent PIXEL POUR PIXEL IDENTIQUE, ACES n'a plus rien à compresser, et
       le néon paraît plat et sale. C'est le prérequis silencieux de toute la
       direction artistique — et rien dans le code ne le rappelle si on
       l'oublie. */
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
      multisampling: 0, // le SMAA du composer s'en charge ; MSAA + post = gâchis
    });

    this.composer.addPass(new RenderPass(scene, camera));

    /* ═══ Couche 3 — l'ONDE DE CHOC ══════════════════════════════════════════
       Sa propre passe, juste après la scène et AVANT le reste de la chaîne : la
       distorsion se propage donc à travers le bloom et l'étalonnage, comme une
       vraie déformation de l'image. La scène overlay (projectiles, joueur, point
       de hitbox) est rendue APRÈS tout le composer — elle n'est donc JAMAIS
       distordue, ce qui protège la lisibilité de la couche danger.

       La passe est DÉSACTIVÉE au repos : quatre effets d'onde qui tournent en
       permanence coûteraient de la bande passante pour rien. On ne l'active que
       le temps d'une onde (voir `shock` et `render`). */
    if (!opts.lowTier) {
      const pool: ShockWaveEffect[] = [];
      for (let i = 0; i < 4; i++) {
        pool.push(
          new ShockWaveEffect(camera, new THREE.Vector3(), {
            speed: SHOCK_SPEED,
            maxRadius: 0.6,
            waveSize: SHOCK_WAVE_SIZE,
            amplitude: 0.05,
          }),
        );
      }
      this.shocks = pool;
      this.shockPass = new EffectPass(camera, ...pool);
      this.shockPass.enabled = false;
      this.composer.addPass(this.shockPass);
    } else {
      this.shocks = [];
      this.shockPass = null;
    }

    this.bloom = new BloomEffect({
      // Seuil, intensité et rayon du plan (§4.6). `luminanceSmoothing` adoucit
      // le seuil et limite le scintillement des petites sources brillantes en
      // mouvement — sans lui, chaque étincelle clignote.
      luminanceThreshold: 0.85,
      luminanceSmoothing: 0.4,
      intensity: 0.55,
      radius: 0.42,
      mipmapBlur: true,
      levels: opts.lowTier ? 1 : 4,
    });

    /* ⚠️ On n'utilise PAS SelectiveBloomEffect. La documentation du mainteneur
       recommande explicitement BloomEffect quand la sélection n'est pas
       nécessaire, car la sélection ajoute une passe de masque. Ici le contrôle
       de ce qui bloome se fait par les VALEURS — émissif au-dessus du seuil —
       ce qui est gratuit, plus contrôlable, et n'a aucune passe à payer.
       Les projectiles ennemis, eux, échappent au bloom par construction :
       ils sont dans la scène overlay, rendue après toute la chaîne. */

    const effects: Array<
      BloomEffect | ChromaticAberrationEffect | ToneMappingEffect | LUT3DEffect | VignetteEffect
    > = [this.bloom];

    if (!opts.lowTier) {
      effects.push(
        new ChromaticAberrationEffect({
          offset: new THREE.Vector2(0.0006, 0.0006),
          radialModulation: true,
          modulationOffset: 0.15,
        }),
      );
    }

    // ACES en toute fin de chaîne, DANS le composer — jamais sur le renderer.
    effects.push(new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC }));

    /* ═══ L'ÉTALONNAGE PAR MONDE ══════════════════════════════════════════
       APRÈS ACES et AVANT la vignette, et les deux positions sont
       contraintes :

       — avant ACES, la table serait indexée sur des valeurs HDR non bornées
         qu'elle ne sait pas représenter, et l'étalonnage dépendrait de
         l'intensité du bloom de la frame ;
       — après la vignette, on étalonnerait un assombrissement, ce qui n'a
         aucun sens et écraserait les coins de l'image.

       ⚠️ Et surtout : la LUT est ICI, c'est-à-dire DANS le composer. La scène
       overlay est rendue après, donc les projectiles ennemis ne la traversent
       jamais — pas plus qu'ils ne traversent ACES. Leur couleur reste
       exactement celle qui a été autorisée, dans les trois mondes. Déplacer la
       LUT hors du composer casserait la propriété n°3 de la couche danger
       (§4.4) sans rien afficher d'anormal avant plusieurs heures de jeu. */
    this.lutData = new Uint8Array(LUT_SIZE * LUT_SIZE * LUT_SIZE * 4);
    worldGrade.advance(0);
    writeLut(this.lutData, worldGrade.current, worldGrade.colorblind);
    this.lut = new THREE.Data3DTexture(this.lutData, LUT_SIZE, LUT_SIZE, LUT_SIZE);
    this.lut.format = THREE.RGBAFormat;
    this.lut.type = THREE.UnsignedByteType;
    this.lut.minFilter = THREE.LinearFilter;
    this.lut.magFilter = THREE.LinearFilter;
    this.lut.wrapS = THREE.ClampToEdgeWrapping;
    this.lut.wrapT = THREE.ClampToEdgeWrapping;
    this.lut.wrapR = THREE.ClampToEdgeWrapping;
    this.lut.needsUpdate = true;
    effects.push(new LUT3DEffect(this.lut));

    effects.push(new VignetteEffect({ offset: 0.32, darkness: 0.55 }));

    // Fusionnés en une seule passe : `postprocessing` concatène les effets
    // compatibles dans un unique shader. C'est tout l'intérêt de la
    // bibliothèque par rapport à une chaîne de passes naïve.
    this.composer.addPass(new EffectPass(camera, ...effects));

    if (!opts.lowTier) {
      this.composer.addPass(new EffectPass(camera, new SMAAEffect()));
    }
  }

  /** À appeler au boot et à chaque redimensionnement RÉEL de la fenêtre —
   *  jamais pour ajuster la qualité (voir `setScale`). */
  setSize(width: number, height: number): void {
    this.maxW = width;
    this.maxH = height;
    this.composer.setSize(width, height);
  }

  /**
   * Resolution scale — SANS le stutter qu'il combat (PLAN.md §4.9).
   *
   * ⚠️ Ne JAMAIS appeler `composer.setSize()` en cours de partie. Chaque appel
   * réalloue le render target principal, la chaîne de mips du bloom et les
   * targets du SMAA : plusieurs allocations GPU et recréations de textures,
   * soit des hitchs de plusieurs millisecondes AU MOMENT PRÉCIS OÙ LE JEU RAME
   * DÉJÀ. Avec des pas de 10 %, on déclenche plusieurs réallocations en
   * quelques secondes, et le mécanisme censé sauver la fluidité devient la
   * première cause de saccade.
   *
   * La technique correcte : tout est alloué à la taille MAX au boot, et seul le
   * VIEWPORT bouge. Une fraction du render target reste inutilisée — de la
   * mémoire, pas de la bande passante. Zéro réallocation, zéro hitch.
   */
  setScale(scale: number): void {
    this.scale = scale < 0.5 ? 0.5 : scale > 1 ? 1 : scale;
  }

  get resolutionScale(): number {
    return this.scale;
  }

  /** `true` dès que `setSize` a été appelé au moins une fois. */
  get sized(): boolean {
    return this.maxW > 0 && this.maxH > 0;
  }

  /** Rend la frame complète : chaîne de post-processing, puis overlay. */
  render(deltaSec: number): void {
    const r = this.renderer;

    /* ⚠️ Sans dimensions, rendre produirait un viewport de taille nulle : un
       écran noir parfait, sans la moindre erreur, alors que tout le reste du
       jeu tourne. C'est exactement la panne survenue au J8 — l'appel à
       `setSize` avait disparu d'une réécriture de `main.ts`, et le symptôme
       (« le 3D est invisible, mais le HUD, l'audio et les menus marchent »)
       n'orientait vers aucune cause évidente.
       On se plaint UNE FOIS, bruyamment, plutôt que de dessiner du vide. */
    if (!this.sized) {
      if (!this.warnedUnsized) {
        this.warnedUnsized = true;
        console.error(
          '[VfxPipeline] render() appelé avant setSize() : rien ne sera dessiné. ' +
            'Appeler vfx.setSize(width, height) au démarrage ET au redimensionnement.',
        );
      }
      return;
    }

    /* L'étalonnage du monde. `advance` ne rend `true` que pendant un fondu —
       deux secondes toutes les trois minutes de jeu — et la reconstruction
       coûte alors 4 096 évaluations, soit quelques dixièmes de milliseconde.
       En régime permanent, ces trois lignes sont une comparaison d'entier.

       ⚠️ On réécrit le CONTENU du tampon déjà alloué, jamais un nouveau. Zéro
       allocation dans le chemin de rendu, et surtout aucun changement de
       `#define` sur l'uber-pass (voir la déclaration de `lut`). */
    if (worldGrade.advance(deltaSec)) {
      writeLut(this.lutData, worldGrade.current, worldGrade.colorblind);
      this.lut.needsUpdate = true;
    }

    // L'onde de choc : sa passe ne tourne que le temps d'une onde vivante. Hors
    // de cette fenêtre, `enabled = false` la retire de la chaîne, coût zéro.
    if (this.shockPass) {
      this.shockTimer -= deltaSec;
      this.shockPass.enabled = this.shockTimer > 0;
    }

    if (this.scale < 1) {
      const w = Math.round(this.maxW * this.scale);
      const h = Math.round(this.maxH * this.scale);
      r.setViewport(0, 0, w, h);
      r.setScissor(0, 0, w, h);
      r.setScissorTest(true);
    } else {
      r.setScissorTest(false);
      r.setViewport(0, 0, this.maxW, this.maxH);
    }

    this.composer.render(deltaSec);

    /* ═══ P5 — la scène OVERLAY ═══════════════════════════════════════════
       Rendue APRÈS toute la chaîne, à la résolution NATIVE, sans effacer le
       tampon de couleur. C'est ce qui donne aux projectiles ennemis leurs trois
       propriétés : jamais bloomés, jamais dégradés par le resolution scale,
       et d'une couleur exactement conforme à celle qui a été autorisée.

       Contrepartie assumée : ils passent après le SMAA, donc sans
       anti-aliasing. Le correctif est un AA analytique dans leur propre shader
       (voir bulletMaterial.ts) — coût nul et résultat supérieur au SMAA sur des
       formes aussi simples. */
    r.setScissorTest(false);
    r.setViewport(0, 0, this.maxW, this.maxH);
    const prevAutoClear = r.autoClear;
    r.autoClear = false;
    r.render(this.overlayScene, this.camera);
    r.autoClear = prevAutoClear;
  }

  /** Modulation temporaire du bloom — utilisée sur les grosses explosions
   *  (PLAN.md §4.6 : +30 % sur 120 ms). Une des trois choses qui remplacent
   *  les vraies lumières pour 2 % du coût. */
  setBloomBoost(factor: number): void {
    this.bloom.intensity = 0.55 * factor;
  }

  /**
   * Déclenche une onde de choc à une position MONDE (le shader la projette via
   * la caméra). Round-robin sur le pool de quatre : une cinquième onde
   * simultanée réutilise la plus ancienne — jamais plus de quatre à l'écran.
   * No-op en tier bas, où le pool n'existe pas.
   *
   * `maxRadius` et `amplitude` sont en unités ÉCRAN : c'est l'appelant qui les
   * dérive de l'ampleur de l'explosion, la vue n'ayant pas à connaître les
   * classes d'explosion. La passe reste active le temps que l'onde BALAYE en
   * entier (voir `shockLifetime`), jamais moins.
   */
  shock(worldX: number, worldY: number, maxRadius: number, amplitude: number): void {
    const s = this.shocks[this.shockCursor];
    if (!s) return;
    this.shockCursor = (this.shockCursor + 1) % this.shocks.length;
    s.position.set(worldX, worldY, 0);
    s.maxRadius = maxRadius;
    s.amplitude = amplitude;
    s.explode();
    const life = shockLifetime(maxRadius);
    if (life > this.shockTimer) this.shockTimer = life;
  }

  dispose(): void {
    this.composer.dispose();
  }
}
